import { useState } from "react";
import { useWatch } from "react-hook-form";
import classNames from "classnames";
import styles from "./index.module.scss";
import { useClientFileContext } from "../../../contexts/ClientFileProvider";
import { Text } from "../../Type";
import ProfilePreview from "../../UserInterface/ProfilePreview";
import CareTeamSelect from "../../CareTeamSelect";
import { UserType } from "../../../types/userType";
import TextLink from "../../TextLinks";
import Button from "../../Button";
import { CheckMarkRounded, X } from "../../Icons/general-interface-1";
import { LocationType } from "../../../types/locationType";
import { cepNewNumber, cepRecycledNumber } from "../../../utils/constants";
import ModalActions from "../../ModalActions";

type PropsType = {
  onSubmit: (input: {
    authorizerId?: string;
    salesRepId: string;
    locationId: string;
    cepNew: boolean;
    cepRecycled: boolean;
  }) => void;
  skip?: boolean;
};

const SignerSelectionForm = ({ onSubmit, skip }: PropsType) => {
  const { clientFile } = useClientFileContext();
  const [step, setStep] = useState(1);
  const [userSelectOpen, setUserSelectOpen] = useState(false);
  const [cepNew, setCepNew] = useState(false);
  const [cepRecycled, setCepRecycled] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationType | null>(
    null
  );
  const [selectedAuthorizer, setSelectedAuthorizer] = useState<UserType | null>(
    null
  );
  const [selectedSalesRep, setSelectedSalesRep] = useState<UserType | null>(
    null
  );
  const [newAuthorizer, setNewAuthorizer] = useState<UserType | null>(null);
  const [newSalesRep, setNewSalesRep] = useState<UserType | null>(null);

  const isCep = useWatch({ name: "section1.cep" }) === "y-cep";
  const clientAuthorizers = clientFile.client.authorizers;
  const clientAuthorizersWithNew =
    newAuthorizer &&
    !clientAuthorizers.some((user) => user.id === newAuthorizer.id)
      ? [...clientAuthorizers, newAuthorizer]
      : clientAuthorizers;
  const clientSalesReps = clientFile.client.sales_reps;
  const selectableSalesReps = clientFile.client.sales_reps.filter((rep) =>
    isCep ? !!rep.cep : true
  );
  const clientSalesRepsWithNew =
    newSalesRep &&
    !selectableSalesReps.some((user) => user.id === newSalesRep.id)
      ? [...selectableSalesReps, newSalesRep]
      : selectableSalesReps;
  const locationOptions = selectedSalesRep?.locations || [];
  const signingLocationId = isCep
    ? ""
    : locationOptions.length === 1
    ? locationOptions[0].id
    : selectedLocation?.id || "";

  const resetSalesRepChoices = () => {
    setCepNew(false);
    setCepRecycled(false);
    setSelectedLocation(null);
  };

  const updateSelectedSalesRep = (rep: UserType | null) => {
    resetSalesRepChoices();
    setSelectedSalesRep(rep);
  };

  const intros = {
    noCepSalesRep: {
      title: "Add the Signing Motion Team Member for this Client",
      description:
        "The care team does not include a CEP enabled Motion Team Member. Please assign a CEP enabled Motion Team Member.",
    },
    selectCepSalesRep: {
      title: "Choose the Signing Motion Team Member for this Client",
      description:
        "Please choose or add the CEP enabled Motion Team Member that will sign the ADP form.",
    },
    selectSalesRep: {
      title: "Choose the Signing Motion Team Member for this Client",
      description:
        "Please choose or add the Motion Team Member that will sign the ADP form.",
    },
    selectAuthorizer: {
      title: "Choose the Signing Authorizer for this Client",
      description:
        "Please choose or add the Authorizer that will sign the ADP form.",
    },
  };

  const flowStep = (() => {
    if (step === 2) {
      return "selectAuthorizer";
    }
    if (isCep && !clientSalesRepsWithNew.length) {
      return "noCepSalesRep";
    }
    if (isCep) {
      return "selectCepSalesRep";
    }
    return "selectSalesRep";
  })();

  const renderIntro = () => (
    <>
      <Text size="large" weight="bold" className={styles.introTitle}>
        {intros[flowStep].title}
      </Text>
      <Text className={styles.introDescription}>
        {intros[flowStep].description}
      </Text>
      <Text size="small" weight="bold" className={styles.selectTitle}>
        {step === 1
          ? `Motion Team Member${clientSalesRepsWithNew.length > 1 ? "s" : ""}`
          : `Authorizer${clientAuthorizersWithNew.length > 1 ? "s" : ""}`}
      </Text>
    </>
  );

  const renderUserButton = (user, selectedUser, setSelected) => (
    <button
      key={user.id}
      type="button"
      onClick={() => {
        setSelected(user);
      }}
      className={classNames(styles.userButton, {
        [styles.userButtonSelected]: user.id === selectedUser?.id,
      })}
    >
      <ProfilePreview size="medium" user={user} userRole />
    </button>
  );

  const renderCepOption = ({ value, setValue, label, number }) => (
    <button
      type="button"
      onClick={() => setValue(!value)}
      className={classNames(styles.cepButton, {
        [styles.cepButtonSelected]: !!value,
      })}
    >
      <span className={styles.cepButtonCheckbox}>
        <CheckMarkRounded color="white" aria-hidden="true" />
      </span>
      <Text component="b" weight="bold" className={styles.cepButtonLabel}>
        {label} Equipment
      </Text>
      <Text component="span" className={styles.cepButtonNumber}>
        ADP Number: {number}
      </Text>
    </button>
  );

  const renderLocationOptions = () => {
    if (locationOptions.length === 1) {
      return (
        <>
          <Text size="large" weight="bold" className={styles.locationTitle}>
            ADP Information
          </Text>
          <div className={styles.inferredLocation}>
            <div className={styles.locationName}>
              <Text weight="bold" className={styles.locationHeading}>
                Location
              </Text>
              <Text className={styles.locationValue}>
                {locationOptions[0].name}
              </Text>
            </div>
            <div className={styles.locationNumber}>
              <Text weight="bold" className={styles.locationHeading}>
                Number
              </Text>
              <Text className={styles.locationValue}>
                {locationOptions[0].adp_auth_num}
              </Text>
            </div>
          </div>
        </>
      );
    }
    if (locationOptions.length > 1) {
      return (
        <>
          <Text size="large" weight="bold" className={styles.locationTitle}>
            Select ADP Information
          </Text>
          <Text className={styles.locationDescription}>
            Please choose the corresponding ADP number associated with this
            form.
          </Text>
          {locationOptions.map((location) => (
            <button
              type="button"
              key={location.id}
              onClick={() => {
                setSelectedLocation(location);
              }}
              className={classNames(styles.locationButton, {
                [styles.locationButtonSelected]:
                  location.id === selectedLocation?.id,
              })}
            >
              <span className={styles.locationButtonRadio}>
                <span className={styles.locationButtonRadioFill} />
              </span>
              <Text
                component="b"
                weight="bold"
                className={styles.locationButtonLabel}
              >
                {location.name}
              </Text>
              <Text component="span" className={styles.locationButtonNumber}>
                {location.adp_auth_num}
              </Text>
            </button>
          ))}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <Text fade size="small" className={styles.step}>
        {step}/2
      </Text>

      {renderIntro()}

      <div className={styles.options}>
        {step === 1 &&
          clientSalesRepsWithNew.length > 0 &&
          clientSalesRepsWithNew.map((rep) =>
            renderUserButton(rep, selectedSalesRep, updateSelectedSalesRep)
          )}
        {step === 2 &&
          clientAuthorizersWithNew.length > 0 &&
          clientAuthorizersWithNew.map((authorizer) =>
            renderUserButton(
              authorizer,
              selectedAuthorizer,
              setSelectedAuthorizer
            )
          )}
      </div>

      {userSelectOpen ? (
        <div className={styles.selectUser}>
          <div className={styles.selectUserHeader}>
            <Text className={styles.selectUserTitle}>
              Add {step === 1 ? "a Motion Team Member" : "an Authorizer"} to the
              Care Team
            </Text>
            <TextLink
              onClick={() => setUserSelectOpen(false)}
              className={styles.selectUserCancel}
            >
              <X /> Cancel
            </TextLink>
          </div>
          <CareTeamSelect
            onChange={(value) => {
              setUserSelectOpen(false);
              const selected = value?.[0];
              if (step === 1) {
                setNewSalesRep(selected || null);
                updateSelectedSalesRep(selected || null);
              } else {
                setNewAuthorizer(selected || null);
                setSelectedAuthorizer(selected || null);
              }
            }}
            roleTypes={step === 1 ? ["sales_rep"] : ["authorizer", "motion_support"]}
            cepOnly={step === 1 && isCep}
            excludedUserIds={
              step === 1
                ? clientSalesReps.map((user) => user.id)
                : clientAuthorizers.map((user) => user.id)
            }
          />
        </div>
      ) : (
        <Text component="p">
          <TextLink onClick={() => setUserSelectOpen(true)}>
            + Add{" "}
            {(step === 1 && !!newSalesRep) || (step === 2 && !!newAuthorizer)
              ? "a different"
              : "a new"}{" "}
            {step === 1 ? "Motion Team Member" : "Authorizer"}
          </TextLink>
        </Text>
      )}

      {step === 1 && !!selectedSalesRep && isCep && (
        <>
          <Text size="large" weight="bold" className={styles.cepTitle}>
            Select CEP Information
          </Text>
          {renderCepOption({
            value: cepNew,
            setValue: setCepNew,
            label: "New",
            number: cepNewNumber,
          })}
          {renderCepOption({
            value: cepRecycled,
            setValue: setCepRecycled,
            label: "Recycled",
            number: cepRecycledNumber,
          })}
        </>
      )}

      {step === 1 && !!selectedSalesRep && !isCep && renderLocationOptions()}

      <ModalActions>
        {step === 1 && (
          <Button
            primary
            disabled={(!selectedSalesRep || (isCep && !cepNew && !cepRecycled) || (!isCep && !signingLocationId))}
            onClick={() => { setStep(2); }}
          >
            Next
          </Button>
        )}
        {step === 2 && (
          <>
            <Button
              primary
              onClick={() => {
                if (!(selectedSalesRep)) {
                  throw new Error("Must select a sales rep");
                }
                onSubmit({
                  authorizerId: selectedAuthorizer?.id ? String(selectedAuthorizer.id) : undefined,
                  salesRepId: selectedSalesRep.id,
                  locationId: signingLocationId,
                  cepNew,
                  cepRecycled,
                });
              }}
              disabled={!(selectedAuthorizer) && !skip}
            >
              {!(selectedAuthorizer) && skip ? `Skip` : `Done`}
            </Button>
            <TextLink
              onClick={() => {
                setStep(1);
              }}
            >
              Back
            </TextLink>
          </>
        )}
      </ModalActions>
    </>
  );
};

export default SignerSelectionForm;
